import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Ban } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { LineProgress, fmtQ } from '@/components/po/PoLinePicker';

const same = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

/** The supplier's open PO rows for a sort no (oldest PO first) and rows without a sort no (they take it on first receipt). */
export const sortRows = (pos, sortNo) => {
  const all = pos.flatMap((po) => po.lines.map((line) => ({ po, line })))
    .sort((a, b) => `${a.po.po_date || ''}${a.po.po_number}`.localeCompare(`${b.po.po_date || ''}${b.po.po_number}`));
  return { mine: all.filter((r) => same(r.line.mapped_sort_no, sortNo)), loose: all.filter((r) => !r.line.mapped_sort_no) };
};

/** null when the qty fits what is still pending on the row; else the "pick the next PO" prompt — only rows that can take the whole bundle (none → blocked). */
export const overflowAsk = ({ row, rows, qty, usedOn, what }) => {
  const left = (Number(row.line.balance_qty) || 0) - usedOn(row.line.po_item_id);
  if (qty <= left + 1e-6) return null;
  const options = (rows || []).filter((r) => r.line.po_item_id !== row.line.po_item_id && r.line.unit === row.line.unit)
    .map((r) => ({ ...r, free: (Number(r.line.balance_qty) || 0) - usedOn(r.line.po_item_id) })).filter((r) => r.free >= qty - 1e-6);
  return { what, qty, left: Math.max(left, 0), row, options };
};

const Notice = ({ tone, children, testId }) => (
  <div data-testid={testId} className={`flex items-start gap-2 rounded-md border p-2 text-xs ${tone === 'red' ? 'border-rose-200 bg-rose-50 text-rose-700' : tone === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{children}</span>
  </div>
);

export const PoRowCard = ({ r, selected, pending = 0, onPick, testId, free }) => (
  <button type="button" onClick={() => onPick(r)} data-testid={testId}
    className={`w-full rounded-md border px-2.5 py-2 text-left transition-colors duration-150 ${selected ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className="font-mono font-semibold text-gray-900">{r.po.po_number}</span>
      {r.po.po_date && <span className="text-[11px] text-gray-500">{r.po.po_date}</span>}
      <span className="min-w-0 flex-1 truncate text-xs text-gray-600">{r.line.supplier_design_name}</span>
      <span className="text-xs font-semibold text-amber-700" data-testid={testId && `${testId}-pending`}>Pending {fmtQ(free ?? r.line.balance_qty)} {r.line.unit}</span>
      <span className="text-xs text-gray-700" data-testid={testId && `${testId}-rate`}>₹{fmtQ(r.line.rate)}/{r.line.unit}</span>
    </div>
    <LineProgress line={r.line} pending={pending} />
  </button>
);

/** Pending PO rows of a supplier for one sort no: PO no, date, pending qty and rate per row (first pending row is picked).
 *  onChange({ po, line, pct, vendorOnPos, none, rows, switched }) — switched = the row in use is no longer open. */
export const SortPoRows = ({ section, vendorName, sortNo, lineId, none, onChange, refreshKey = 0, pendingOf = () => 0, allowNone = true, testPrefix = 'po' }) => {
  const [data, setData] = useState(null);
  const cb = useRef(onChange);
  cb.current = onChange;
  const cur = useRef({ lineId, none });
  cur.current = { lineId, none };
  useEffect(() => {
    let live = true;
    setData(null);
    if (!vendorName) return undefined;
    apiClient.get('/po-receipts/open-pos', { params: { vendor_name: vendorName, section } })
      .then((r) => live && setData(r.data)).catch(() => live && setData({ pos: [], vendor_on_pos: false, over_receipt_pct: 5 }));
    return () => { live = false; };
  }, [vendorName, section, refreshKey]);
  const { mine, loose } = useMemo(() => (data && sortNo ? sortRows(data.pos, sortNo) : { mine: [], loose: [] }), [data, sortNo]);
  const base = data ? { pct: data.over_receipt_pct, vendorOnPos: data.vendor_on_pos, rows: [...mine, ...loose] } : null;
  useEffect(() => {
    if (!data) return;
    const { lineId: id, none: nn } = cur.current;
    const keep = [...mine, ...loose].find((r) => r.line.po_item_id === id);
    const pick = keep || (nn ? null : (mine.find((r) => r.line.balance_qty > 1e-6) || mine[0] || null));
    cb.current({ pct: data.over_receipt_pct, vendorOnPos: data.vendor_on_pos, rows: [...mine, ...loose],
      po: pick?.po || null, line: pick?.line || null, none: !pick && !!nn, switched: !!id && !keep });
  }, [data, mine, loose]);
  if (!vendorName) return null;
  if (!sortNo) return <p className="text-xs text-gray-500" data-testid={`${testPrefix}-need-sort`}>Pick the sort no — its pending Purchase Orders show here.</p>;
  if (!data) return <p className="text-xs text-gray-500" data-testid={`${testPrefix}-loading`}>Loading Purchase Orders…</p>;
  if (!data.pos.length) {
    return data.vendor_on_pos
      ? <Notice tone="red" testId={`${testPrefix}-none-open`}>No open {section} Purchase Order for {vendorName}. Supplier deliveries can't be counted — ask Purchase to issue a PO. Returns / transfers can still be assigned.</Notice>
      : <Notice testId={`${testPrefix}-no-pos`}>{vendorName} has no {section} Purchase Orders — bundles are assigned without a PO.</Notice>;
  }
  const pickRow = (r) => onChange({ ...base, po: r.po, line: r.line, none: false });
  const card = (r) => <PoRowCard key={r.line.po_item_id} r={r} selected={r.line.po_item_id === lineId} pending={pendingOf(r.line.po_item_id)} onPick={pickRow}
    free={(Number(r.line.balance_qty) || 0) - pendingOf(r.line.po_item_id)} testId={`${testPrefix}-row-${r.line.po_item_id}`} />;
  return (
    <div className="space-y-1.5" data-testid={`${testPrefix}-rows`}>
      {!mine.length && <Notice tone="amber" testId={`${testPrefix}-no-rows`}>No pending Purchase Order row for sort no {sortNo}{loose.length ? ' — pick a row without a sort no below' : ''}.</Notice>}
      <div className="grid max-h-72 gap-1.5 overflow-y-auto pr-0.5">
        {mine.map(card)}
        {loose.length > 0 && <p className="pt-1 text-[11px] font-medium text-gray-500">Rows without a sort no — they take {sortNo} on the first receipt</p>}
        {loose.map(card)}
      </div>
      {allowNone && (
        <button type="button" onClick={() => onChange({ ...base, po: null, line: null, none: true })} data-testid={`${testPrefix}-row-none`}
          className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors ${none ? 'border-gray-700 bg-gray-100 text-gray-900' : 'border-dashed border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
          <Ban className="h-3.5 w-3.5" />No PO — returns / transfers only</button>)}
    </div>
  );
};

export default SortPoRows;
